import {
  bigint,
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { relations, sql } from 'drizzle-orm';
import type { BackfillStep } from '../types';
import type { PullFileSummary, WalkthroughGroup } from '../pull';
import type { ReviewConfigType } from '../review';
import type { OrganizationConfig } from '../config';

// The order of these enums is important for sorting
export const PullStateEnum = pgEnum('state', ['OPEN', 'DRAFT', 'MERGED', 'CLOSED']);
export const PullReviewDecisionEnum = pgEnum('review_decision', ['APPROVED', 'CHANGES_REQUESTED', 'REVIEW_REQUIRED']);
export const PullReviewStateEnum = pgEnum('review_state', [
  'PENDING',
  'COMMENTED',
  'APPROVED',
  'CHANGES_REQUESTED',
  'DISMISSED',
]);
export const PullCheckStatusEnum = pgEnum('check_status', ['PENDING', 'SUCCESS', 'FAILURE', 'ERROR', 'EXPECTED']);
export const AuthorAssociationEnum = pgEnum('author_association', [
  'COLLABORATOR',
  'CONTRIBUTOR',
  'FIRST_TIMER',
  'FIRST_TIME_CONTRIBUTOR',
  'MANNEQUIN',
  'MEMBER',
  'NONE',
  'OWNER',
]);
export const MergeableStateEnum = pgEnum('mergeable_state', [
  'CLEAN',
  'BLOCKED',
  'DIRTY',
  'BEHIND',
  'UNSTABLE',
  'HAS_HOOKS',
  'DRAFT',
  'UNKNOWN',
]);

export const PullsTable = pgTable(
  'pulls',
  {
    nodeId: text('node_id').primaryKey(),
    id: bigint('id', { mode: 'number' }).unique(),
    owner: text('owner').notNull(),
    repo: text('repo').notNull(),
    number: integer('number').notNull(),
    title: text('title').notNull(),
    body: text('body').notNull(),
    state: PullStateEnum('state').notNull(),
    reviewDecision: PullReviewDecisionEnum('review_decision'),
    mergeable: boolean('mergeable'),
    rebaseable: boolean('rebaseable'),
    mergeableState: MergeableStateEnum('mergeable_state'),
    additions: integer('additions').notNull(),
    deletions: integer('deletions').notNull(),
    author: jsonb('author')
      .$type<{
        id: number;
        login: string;
        name?: string | null | undefined;
        avatarUrl: string;
      }>()
      .notNull(),
    authorAssociation: AuthorAssociationEnum('author_association'),
    labels: jsonb('labels')
      .$type<
        {
          name: string;
          color: string;
        }[]
      >()
      .default([]),
    head: jsonb('head')
      .$type<{
        ref: string;
        sha: string;
      }>()
      .notNull(),
    base: jsonb('base')
      .$type<{
        ref: string;
        sha: string;
      }>()
      .notNull(),
    requestedReviewers: jsonb('requested_reviewers')
      .$type<
        {
          id: number;
          login?: string | null | undefined;
          name?: string | null | undefined;
          avatarUrl?: string | null | undefined;
        }[]
      >()
      .default([]),
    assignees: jsonb('assignees')
      .$type<
        {
          id: number;
          login: string;
          avatarUrl: string;
        }[]
      >()
      .default([]),

    checkStatusRollup: PullCheckStatusEnum('check_status_rollup'),
    commentCount: integer('comment_count').notNull().default(0),
    mergedBy: jsonb('merged_by').$type<{
      id: number;
      login: string;
      name?: string | null | undefined;
      avatarUrl?: string | null | undefined;
    }>(),
    mergeCommitSha: text('merge_commit_sha'),
    closedAt: text('closed_at'),
    mergedAt: text('merged_at'),
    createdAt: text('created_at').notNull(),
    updatedAt: text('updated_at').notNull(),
  },
  (t) => [uniqueIndex('unique_pull').on(t.owner, t.repo, t.number)]
);

export const PullDiffsTable = pgTable(
  'pull_diffs',
  {
    uuid: uuid('uuid').defaultRandom().primaryKey(),
    id: bigint('id', { mode: 'number' }).notNull().unique(),
    owner: text('owner').notNull(),
    repo: text('repo').notNull(),
    number: integer('number').notNull(),
    commitSha: text('commit_sha').notNull(),
    diff: text('diff').notNull(),
    fileSummaries: jsonb('file_summaries').$type<PullFileSummary[]>(),
    groupedDiffs: jsonb('grouped_diffs').$type<WalkthroughGroup[]>(),
    isGenerating: boolean('is_generating').notNull().default(true),
  },
  (t) => [uniqueIndex('unique_pull_diff').on(t.owner, t.repo, t.number, t.commitSha)]
);

export const PullRelations = relations(PullsTable, ({ one }) => ({
  diffs: one(PullDiffsTable, {
    fields: [PullsTable.id],
    references: [PullDiffsTable.id],
  }),
}));

export const CheckSuiteConclusionEnum = pgEnum('check_suite_conclusion', [
  'SUCCESS',
  'FAILURE',
  'NEUTRAL',
  'CANCELLED',
  'SKIPPED',
  'TIMED_OUT',
  'ACTION_REQUIRED',
  'STALE',
]);
export const CheckSuiteStatusEnum = pgEnum('check_suite_status', ['COMPLETED', 'IN_PROGRESS', 'REQUESTED', 'QUEUED']);
export const CheckSuiteTable = pgTable('check_suites', {
  nodeId: text('node_id').primaryKey(),
  id: bigint('id', { mode: 'number' }).unique(),

  conclusion: CheckSuiteConclusionEnum('conclusion'),
  status: CheckSuiteStatusEnum('status'),
  rerequestable: boolean('rerequestable').default(true),
  runs_rerequestable: boolean('runs_rerequestable').default(true),
  latest_check_runs_count: integer('latest_check_runs_count').default(0),

  headBranch: text('head_branch'),
  headSha: text('head_sha').notNull(),
  before: text('before'),
  after: text('after'),

  check_runs_url: text('check_runs_url'),
  createdAt: text('created_at').notNull(),
  updatedAt: text('updated_at').notNull(),

  installationId: integer('installation_id'),
  owner: text('owner').notNull(),
  repository: text('repository').notNull(),
});

export const CheckRunConclusionEnum = pgEnum('check_run_conclusion', [
  'WAITING',
  'PENDING',
  'STARTUP_FAILURE',
  'STALE',
  'SUCCESS',
  'FAILURE',
  'NEUTRAL',
  'CANCELLED',
  'SKIPPED',
  'TIMED_OUT',
  'ACTION_REQUIRED',
]);
export const CheckRunStatusEnum = pgEnum('check_run_status', ['QUEUED', 'IN_PROGRESS', 'COMPLETED', 'PENDING']);
export const CheckRunsTable = pgTable('check_runs', {
  nodeId: text('node_id').primaryKey(),
  id: bigint('id', { mode: 'number' }).unique(),
  name: text('name').notNull(),

  conclusion: CheckRunConclusionEnum('conclusion'),
  status: CheckRunStatusEnum('status').notNull(),

  headSha: text('head_sha').notNull(),

  startedAt: text('started_at').notNull(),
  completedAt: text('completed_at'),
  htmlUrl: text('html_url').notNull(),

  checkSuiteId: bigint('check_suite_id', { mode: 'number' }).references(() => CheckSuiteTable.id),
  installationId: integer('installation_id'),
  owner: text('owner').notNull(),
  repository: text('repository').notNull(),
});

export const CheckSuiteRelations = relations(CheckSuiteTable, ({ many }) => ({
  checkRuns: many(CheckRunsTable),
}));

export const CheckRunRelations = relations(CheckRunsTable, ({ one }) => ({
  checkSuite: one(CheckSuiteTable, {
    fields: [CheckRunsTable.checkSuiteId],
    references: [CheckSuiteTable.id],
  }),
}));

export const IssueCommentsTable = pgTable('issue_comments', {
  gid: text('gid').primaryKey(),
  id: bigint('id', { mode: 'number' }).unique(),
  body: text('body').notNull(),
  authorLogin: text('author_login').notNull(),
  author: jsonb('author')
    .$type<{
      login: string;
      id: number;
      avatarUrl: string;
    }>()
    .notNull(),
  authorAssociation: AuthorAssociationEnum('author_association'),

  owner: text('owner').notNull(),
  repo: text('repo').notNull(),
  issueNumber: integer('issue_number').notNull(),
  createdAt: text('created_at').notNull(),
  updatedAt: text('updated_at').notNull(),
});

export const ReviewCommentSideEnum = pgEnum('review_comment_side', ['LEFT', 'RIGHT']);
export const ReviewCommentTypeEnum = pgEnum('review_comment_type', [
  'Logic',
  'Style',
  'Security',
  'Performance',
  'Question',
  'Other',
]);
export const ReviewCommentSeverityEnum = pgEnum('review_comment_severity', ['Low', 'Medium', 'High']);
export const CommentTypeEnum = pgEnum('comment_type', ['issue', 'review', 'review_body']);
export type CommentType = (typeof CommentTypeEnum.enumValues)[number];
export const ReactionEmojiEnum = pgEnum('reaction_emoji', ['👍', '👎', '😄', '🎉', '😕', '❤️', '🚀', '👀']);
export const ReviewCommentsTable = pgTable(
  'review_comments',
  {
    gid: text('gid').primaryKey(),
    id: bigint('id', { mode: 'number' }).unique(),
    body: text('body').notNull(),
    authorLogin: text('author_login').notNull(),
    author: jsonb('author')
      .$type<{
        login: string;
        id: number;
        avatarUrl: string;
      }>()
      .notNull(),
    authorAssociation: AuthorAssociationEnum('author_association'),
    commitId: text('commit_id').notNull(),
    inReplyToId: bigint('in_reply_to_id', { mode: 'number' }),
    path: text('path').notNull(),
    startLine: integer('start_line'),
    line: integer('line'),
    side: ReviewCommentSideEnum('side').notNull(),

    type: ReviewCommentTypeEnum('type').default('Other'),
    severity: ReviewCommentSeverityEnum('severity'),
    isBad: boolean('is_bad').default(false),

    reviewId: text('review_id').references(() => ReviewTable.gid),
    externalReviewId: bigint('external_review_id', { mode: 'number' }),
    resolvedById: integer('resolved_by_id'),
    isOutdated: boolean('is_outdated').default(false),
    owner: text('owner').notNull(),
    repo: text('repo').notNull(),
    pullNumber: integer('pull_number').notNull(),
    createdAt: text('created_at').notNull(),
    updatedAt: text('updated_at').notNull(),
  },
  (t) => [index('idx_review_comments_resolved_by_id').on(t.resolvedById)]
);

export const ReactionsTable = pgTable(
  'reactions',
  {
    gid: text('gid').primaryKey(), // Zero primary key
    id: bigint('id', { mode: 'number' }).unique(), // GitHub reaction ID (null for optimistic)
    commentId: bigint('comment_id', { mode: 'number' }).notNull(),
    commentType: CommentTypeEnum('comment_type').notNull(),
    userId: bigint('user_id', { mode: 'number' }).notNull(), // GitHub user ID
    emoji: ReactionEmojiEnum('emoji').notNull(), // Unicode emoji for the reaction
  },
  (t) => [
    index('idx_reactions_comment_id_type').on(t.commentId, t.commentType),
    index('idx_reactions_user_id').on(t.userId),
    // Unique constraint to prevent duplicate reactions
    uniqueIndex('unique_user_reaction').on(t.commentId, t.commentType, t.userId, t.emoji),
  ]
);

export const BackfillStatusEnum = pgEnum('backfill_status_enum', ['waiting', 'running', 'completed']);
export const BackfillStatusTable = pgTable('backfill_status', {
  id: text('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  status: BackfillStatusEnum('status').notNull().default('waiting'),
  steps: jsonb('steps').$type<BackfillStep[]>().notNull().default([]),
  errors: jsonb('errors').$type<string[]>().default([]),

  installationId: integer('installation_id').notNull().unique(),
  completedAt: text('completed_at'),
  createdAt: text('created_at')
    .notNull()
    .default(sql`now()`),
});

// defaultReviewConfig is declared in 4 places in the repo
// Make sure you update all 4 places if you change this
// We will DRY this later. This is difficult to do right now due to circular dependencies
// export const defaultReviewConfig: ReviewConfigType = {
//   enabled: true,
//   syncReviewCommentsToGitHub: true,
//   autoApprove: {
//     enabled: false,
//     labels: ['minor-fix'],
//   },
//   commentTypes: {
//     Logic: true,
//     Style: false,
//     Security: true,
//     Performance: true,
//     Question: false,
//     Other: false,
//   },
//   commentSeverities: {
//     Low: false,
//     Medium: false,
//     High: true,
//   },
// };

export const OrgConfigTable = pgTable('org_configs', {
  installationId: bigint('installation_id', { mode: 'number' }).notNull(),
  owner: text('owner').primaryKey(),
  config: jsonb('config').$type<OrganizationConfig>().notNull(),
  updatedAt: text('updated_at')
    .notNull()
    .default(sql`now()`),
});

export const ReviewTable = pgTable('reviews', {
  gid: text('gid').primaryKey(),
  id: bigint('id', { mode: 'number' }).unique().notNull(),
  nodeId: text('node_id').notNull().unique(),
  state: PullReviewStateEnum('state').notNull(),
  commitId: text('commit_id').notNull(),
  body: text('body').notNull(),

  agentVersion: text('agent_version'),

  author: jsonb('author').$type<{
    login: string;
    id: number;
    avatarUrl: string;
  }>(),
  authorAssociation: AuthorAssociationEnum('author_association'),

  installationId: bigint('installation_id', { mode: 'number' }),
  owner: text('owner').notNull(),
  repo: text('repo').notNull(),
  pullNumber: integer('pull_number').notNull(),

  createdAt: text('created_at')
    .notNull()
    .default(sql`now()`),
  updatedAt: text('updated_at')
    .notNull()
    .default(sql`now()`),
});

export const ReviewRelations = relations(ReviewTable, ({ many }) => ({
  comments: many(ReviewCommentsTable),
}));

export const ReviewCommentRelations = relations(ReviewCommentsTable, ({ one }) => ({
  review: one(ReviewTable, {
    fields: [ReviewCommentsTable.reviewId],
    references: [ReviewTable.gid],
  }),
}));

export const BranchPushesTable = pgTable(
  'branch_pushes',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    owner: text('owner').notNull(),
    repo: text('repo').notNull(),
    branch: text('branch').notNull(),
    pusherLogin: text('pusher_login').notNull(),
    pusherId: bigint('pusher_id', { mode: 'number' }).notNull(),
    headSha: text('head_sha').notNull(),
    latestCommitMessage: text('latest_commit_message'),
    latestCommitAuthor: text('latest_commit_author'),
    pushedAt: timestamp('pushed_at').notNull(),
    dismissed: boolean('dismissed').default(false),
    pullRequestCreated: boolean('pull_request_created').default(false),
    pullRequestNumber: integer('pull_request_number'),
    defaultBranch: text('default_branch').notNull().default('main'),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (t) => [
    index('idx_branch_pushes_user_date').on(t.pusherLogin, t.pushedAt),
    uniqueIndex('idx_branch_pushes_unique').on(t.owner, t.repo, t.branch, t.pusherLogin),
  ]
);

export const UserPreferencesTable = pgTable(
  'user_preferences',
  {
    id: text('id').primaryKey().default(sql`gen_random_uuid()`),
    userId: text('user_id').notNull(),
    organizationId: text('organization_id').notNull(), // Organization login/slug
    
    // Layout-specific fields
    layoutType: text('layout_type').notNull(),    // 'pulls_table', 'issues_table', etc.
    layoutKey: text('layout_key').notNull(),      // 'columns.hidden', 'columns.order', 'columns.widths'
    layoutValue: text('layout_value').notNull(),  // The actual value as a string
    
    createdAt: text('created_at').notNull().default(sql`CURRENT_TIMESTAMP`),
    updatedAt: text('updated_at').notNull().default(sql`CURRENT_TIMESTAMP`),
  },
  (t) => [
    // Composite unique constraint ensures one layout setting per user per org
    uniqueIndex('unique_user_layout').on(t.userId, t.organizationId, t.layoutType, t.layoutKey),
  ]
);
